import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { attr, type SafeHtml } from 'kerfjs';

export const UNSAVED_CHANGES_DIALOG_ACTIONS = {
  dialog: attr('data-action', 'unsaved-dialog'),
  keep: attr('data-action', 'unsaved-keep'),
  discard: attr('data-action', 'unsaved-discard'),
  save: attr('data-action', 'unsaved-save'),
} as const;

/**
 * Asked when the viewer leaves a settings page with unsaved edits (HS2-HV4R7C):
 * Keep editing stays on the page (as do Escape and an outside press), Discard
 * puts the saved values back and continues, and Save saves first and
 * continues only if the save succeeds. Rendered open by the shell while a
 * navigation is held. With `closing`, it asks the same about a dialog the viewer
 * is closing (HS2-FSBQVX); `pageLabel` then names what the dialog edits.
 */
export function UnsavedChangesDialog({
  pageLabel,
  saving = false,
  closing = false,
}: {
  pageLabel: string;
  saving?: boolean;
  closing?: boolean;
}): SafeHtml {
  return (
    <DialogSurface
      label="Save changes?"
      lightDismiss
      className="unsaved-changes-dialog"
      rootAttributes={{ 'data-action': UNSAVED_CHANGES_DIALOG_ACTIONS.dialog.value, 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button size="default" tone="neutral" emphasis="plain" action={UNSAVED_CHANGES_DIALOG_ACTIONS.keep.value}>
            Keep Editing
          </Button>
          <Spacer flex />
          <Button
            size="default"
            tone="danger"
            emphasis="plain"
            disabled={saving ? true : undefined}
            action={UNSAVED_CHANGES_DIALOG_ACTIONS.discard.value}
          >
            Discard
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            loading={saving ? true : undefined}
            action={UNSAVED_CHANGES_DIALOG_ACTIONS.save.value}
          >
            Save
          </Button>
        </>
      }
    >
      <List>
        <ListInsetControl sides="rl">
          {closing ? (
            <Text>You have unsaved changes to {pageLabel}. Save them before you close, or discard them.</Text>
          ) : (
            <Text>
              You have unsaved changes to <strong>{pageLabel}</strong>. Save them before you leave, or discard them.
            </Text>
          )}
        </ListInsetControl>
      </List>
    </DialogSurface>
  );
}
